import React from "react";
import { array, option } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { TStaffUser } from "../../../../domain/codecs/User";
import {
    isChatAssignmentAction,
    isChatAwaitAction,
    isRoadblockClosedAction,
    isRoadblockOpenAction,
    isSignOffInvalidationAction,
} from "../../../../domain/codecs/form/RoadblockForm";
import { TCaseTimelineRoadblockContext } from "../../../../domain/codecs/form/CaseTimelineForm";
import { TTimelineEvent } from "./CRMCaseTimelineGroupingLogic";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Padding } from "../BuildingBlocks/Padding";
import { FontQuicksand } from "../BuildingBlocks/FontQuicksand";
import { Text } from "../BuildingBlocks/Text";
import { CRMIcon } from "../CRMIcon/CRMIcon";
import { TIcon } from "../CRMIcon/CRMIcon";
import { CRMColors } from "../../models/CRMColors";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { TCRMMainColours } from "../../models/TCRMMainColours";
import { THexColor } from "../../models/StringLiterals";
import { camelCaseToCopyText } from "../../../../shared/src/util";
import { getTimeUntilCopyText, formatDateCurried } from "../../../../shared/src/utilsByDomain/dateTime";
import { capitaliseFirst, underscoreCaseToNormalCase } from "../../../../shared/src/utilsByDomain/string";

type TCaseTimelineEventLineProps = {
    participants: Array<TStaffUser>;
    event: TTimelineEvent;
    onOpenRoadblock: (roadblock: TCaseTimelineRoadblockContext) => void;
};

type TEventDisplay = { icon: TIcon; iconColour: TCRMMainColours; iconBg: THexColor; label: string };

export const CRMCaseTimelineEventLine = (props: React.PropsWithChildren<TCaseTimelineEventLineProps>): JSX.Element => {

    const display = getEventDisplay(props.event, props.participants);
    const time = formatDateCurried("h:mm a")(props.event.date).toLowerCase();
    const roadblockName = pipe(
        underscoreCaseToNormalCase(props.event.roadblock.roadblock_type),
        capitaliseFirst
    );

    return (
        <Padding type="custom" spacing={`4px ${CRMSpacing.MEDIUM}`}>
            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center" childSize="auto 1fr auto">
                <Background color={display.iconBg} borderRadius="5px" padding="3px" display="inline-flex" alignItems="center" justifyContent="center">
                    <CRMIcon iconName={display.icon} size="14px" colour={display.iconColour} />
                </Background>

                <FontQuicksand>
                    <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2} weight={500}>
                        {display.label}
                    </Text>
                </FontQuicksand>

                <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                    <Background
                        borderRadius="6px"
                        padding={`1px ${CRMSpacing.TINY}`}
                        cursor="pointer"
                        onClick={() => props.onOpenRoadblock(props.event.roadblock)}
                    >
                        <FontQuicksand>
                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4} weight={600}>
                                {roadblockName}
                            </Text>
                        </FontQuicksand>
                    </Background>
                    <FontQuicksand>
                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4} weight={500}>
                            {time}
                        </Text>
                    </FontQuicksand>
                </SpacingRow>
            </SpacingRow>
        </Padding>
    );
};

const getEventDisplay = (event: TTimelineEvent, participants: Array<TStaffUser>): TEventDisplay => {
    const entry = event.entry;

    if (isRoadblockOpenAction(entry)) {
        return { icon: "add", iconColour: "primary-2", iconBg: CRMColors.PRIMARY_4, label: "Roadblock opened" };
    }
    if (isRoadblockClosedAction(entry)) {
        return entry.action.closed_type === "resolved"
            ? { icon: "done", iconColour: "highlights-positive-green-2", iconBg: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2, label: "Roadblock resolved" }
            : { icon: "pause", iconColour: "neutral-2", iconBg: CRMColors.NEUTRAL_8, label: "Roadblock suspended" };
    }
    if (isChatAssignmentAction(entry)) {
        const name = pipe(
            participants,
            array.findFirst((participant) => participant.id === entry.action.assigned_to_user_id),
            option.fold(() => "a colleague", (user) => `${user.first_name} ${user.last_name}`)
        );
        return { icon: "person-tick", iconColour: "primary-2", iconBg: CRMColors.PRIMARY_4, label: `Assigned to ${name}` };
    }
    if (isChatAwaitAction(entry)) {
        return { icon: "hourglass", iconColour: "neutral-2", iconBg: CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_4, label: `Awaiting the ${camelCaseToCopyText(entry.action.blocked_by)} for ${getTimeUntilCopyText(entry.action.blocked_until)}` };
    }
    if (isSignOffInvalidationAction(entry)) {
        return { icon: "backspace", iconColour: "neutral-2", iconBg: CRMColors.NEUTRAL_8, label: "Sign-off removed" };
    }
    return { icon: "clock", iconColour: "neutral-2", iconBg: CRMColors.NEUTRAL_8, label: "Update" };
};
